import {FC, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import JSZip from 'jszip'

type IProps = {
  id: string
  label?: string
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  value?: File | string | null
  errorMsg?: string
  hasError?: boolean
  isRequired: boolean
  hint?: string
  setField: React.Dispatch<React.SetStateAction<{error: boolean; value: File | string | null}>>
}

const BulkImageField: FC<IProps> = ({id, errorMsg, hint, hasError, setField}) => {
  const {formatMessage} = useIntl()
  const defaultFileName = formatMessage({id: 'upload_from_computer'})
  const inputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState<boolean>(false)
  const [hasImageError, setHasImageError] = useState<boolean>(false)
  const [hasFileSizeError, setHasFileSizeError] = useState<boolean>(false)
  const [hasFileNameError, setHasFileNameError] = useState<boolean>(false)
  const [fileSizeErrorFiles, setFileSizeErrorFiles] = useState<string[]>([])
  const [imageDimensionErrorFiles, setImageDimensionErrorFiles] = useState<string[]>([])
  const [fileNameErrorFiles, setFileNameErrorFiles] = useState<string[]>([])

  const HintLabel: FC = () => (
    <span dangerouslySetInnerHTML={{__html: formatMessage({id: 'upload_image_hint'})}}></span>
  )
  const FileSizeHint: FC = () => (
    <span dangerouslySetInnerHTML={{__html: formatMessage({id: 'file_size_hint'})}}></span>
  )

  // Unchanged validation: ZIP only, < 1GB, each entry must match barcode-NN.ext.
  const processFile = async (file?: File | null) => {
    if (file) {
      const maxSizeInBytes = 1024 * 1024 * 1024
      const fileNameLocal = file.name
      const fileExtension = fileNameLocal.slice(fileNameLocal.lastIndexOf('.'))

      setFileSizeErrorFiles([])
      setImageDimensionErrorFiles([])
      setFileNameErrorFiles([])
      if (file.size > maxSizeInBytes) {
        setFileSizeErrorFiles((prev) => [...prev, file.name])
        setHasFileSizeError(true)
      } else {
        setHasFileSizeError(false)
      }
      if (fileExtension === '.zip') {
        const zip = new JSZip()
        const zipContent = await zip.loadAsync(file)
        const imageValidationPromises = Object.keys(zipContent.files).map(async (filePath) => {
          const zipEntry = zipContent.files[filePath]
          if (!zipEntry.dir) {
            const fileContent = await zipEntry.async('blob')
            const img = new Image()
            const baseFileName = filePath.split('/').pop() || ''
            // Barcode = everything before the trailing "-NN" index (greedy), so
            // barcodes containing hyphens (e.g. "SKT147-41-01.webp") are accepted.
            const fileNamePattern = /^.+-\d{2}\.(jpeg|jpg|png|gif|webp)$/i
            if (!fileNamePattern.test(baseFileName)) {
              setFileNameErrorFiles((prev) => [...prev, baseFileName])
              setHasFileNameError(true)
            }
            return new Promise<void>((resolve) => {
              img.onload = () => {
                resolve()
              }
              img.onerror = () => resolve()
              img.src = URL.createObjectURL(fileContent)
            })
          }
        })
        await Promise.all(imageValidationPromises)
        if (fileSizeErrorFiles.length > 0 || imageDimensionErrorFiles.length > 0 || fileNameErrorFiles.length > 0) {
          setField({error: true, value: ''})
          setFileName(null)
        } else {
          setField({error: false, value: file})
          setFileName(file.name)
        }
      } else {
        setFileSizeErrorFiles((prev) => [...prev, file.name])
        setField({error: true, value: ''})
        setFileName(null)
      }
    } else {
      setField({error: true, value: ''})
      setFileName(null)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    processFile(e.currentTarget.files?.[0] ?? null)
  }
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    processFile(e.dataTransfer.files?.[0] ?? null)
  }

  const openPicker = () => inputRef.current?.click()
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      openPicker()
    }
  }

  const invalid = Boolean(hasError || hasFileSizeError || hasFileNameError || hasImageError)

  return (
    <>
      <div
        role='button'
        tabIndex={0}
        className={`apb-dropzone${isDragOver ? ' is-dragover' : ''}${invalid ? ' is-invalid' : ''}`}
        onClick={openPicker}
        onKeyDown={handleKeyDown}
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragOver(true)
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        <div className='apb-dropzone-icon apb-dropzone-icon--secondary'>
          <i className='fas fa-cloud-arrow-up' aria-hidden='true' />
        </div>
        <p className='apb-dropzone-title'>{fileName || defaultFileName}</p>
        <p className='apb-dropzone-hint'>{formatMessage({id: 'images_zip_hint'})}</p>
        <input ref={inputRef} type='file' accept='.zip' id={id} onChange={handleChange} />
      </div>

      {/* Naming guide — always visible, mirrors the existing hint copy. */}
      <div className='apb-info-box'>
        <i className='fas fa-circle-info' aria-hidden='true' />
        <div className='apb-info-content'>
          <p>{hint ? hint : <HintLabel />}</p>
          <p>
            <FileSizeHint />
          </p>
        </div>
      </div>

      {hasError && !hasImageError && !hasFileSizeError && !hasFileNameError && (
        <span className='apb-error'>{errorMsg ? errorMsg : formatMessage({id: 'images_required'})}</span>
      )}
      {hasFileSizeError && fileSizeErrorFiles.length > 0 && (
        <span className='apb-error'>
          {formatMessage({id: 'file_size_exceeded'}, {files: fileSizeErrorFiles.join(', ')})}
        </span>
      )}
      {hasFileNameError && fileNameErrorFiles.length > 0 && (
        <span className='apb-error'>
          {formatMessage({id: 'invalid_file_name_format'}, {files: fileNameErrorFiles.join(', ')})}
        </span>
      )}
    </>
  )
}

export default BulkImageField
